import { supabase } from '../../shared/supabase/supabaseClient';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { accentQueryVariants } from '../../shared/format/searchText';
import type { CustomersSnapshotPayload } from '../../sync/core/customersRpcClient';
import type { RemoteCustomerRow } from '../../sync/core/customerPullDomain';
import {
  countCustomerSnapshotRows,
  fetchCustomerAccountsFromSnapshot,
  mergeCustomerSnapshotWithLocal,
} from './filterCustomersFromSnapshot';
import {
  bumpWebCustomerSnapshotGeneration,
  getWebCustomerSnapshotCache,
  loadWebCustomerSnapshot,
} from './webCustomerSnapshotCache';
import { buildDebtSinceByCustomerIdFromSnapshot } from '../application/customerDebtSince';
import type {
  CustomerAccountListQuery,
  CustomerAccountRow,
  CustomerMovementHistoryFilters,
  CustomerMovementRow,
} from '../domain/customerTypes';

function appendRemoteCustomerSearchOr(
  search: string,
  extraCustomerIds?: string[]
): string {
  const variants = accentQueryVariants(search);
  const parts = variants.flatMap((v) => [
    `name.ilike.%${v}%`,
    `phone.ilike.%${v}%`,
    `cuit.ilike.%${v}%`,
  ]);
  // Teléfonos se guardan E.164 ("+5491116547120") pero el usuario busca
  // "1654-7120" / "1654 7120": agregar una variante solo-dígitos para
  // phone/cuit. >=3 dígitos para no amplificar ruido.
  const digits = search.replace(/\D+/g, '');
  if (digits.length >= 3) {
    parts.push(`phone.ilike.%${digits}%`, `cuit.ilike.%${digits}%`);
  }
  const ids = (extraCustomerIds ?? []).filter(Boolean);
  if (ids.length > 0) parts.push(`id.in.(${ids.join(',')})`);
  return parts.join(',');
}

/**
 * Tras un write web: invalida pulls en vuelo sin borrar la lista.
 * Alta/edición hacen upsert aparte; cargos/pagos fuerzan refresh.
 */
export function invalidateWebCustomerSnapshotAfterWrite(): void {
  bumpWebCustomerSnapshotGeneration();
}

/** Fuerza re-fetch completo (saldos / movimientos). No bloquea si falla. */
export async function refreshWebCustomerSnapshotAfterWrite(): Promise<void> {
  bumpWebCustomerSnapshotGeneration();
  try {
    await loadWebCustomerSnapshot(true);
  } catch (error) {
    console.warn('[fiadoRepository] refreshWebCustomerSnapshotAfterWrite', error);
  }
}

async function loadWebCustomerListSnapshot(): Promise<CustomersSnapshotPayload | null> {
  try {
    const snapshot = await loadWebCustomerSnapshot();
    if (snapshot.snapshot_complete === false) return null;
    return snapshot;
  } catch (error) {
    console.warn('[fiadoRepository] loadWebCustomerListSnapshot', error);
    return null;
  }
}

export const mapCustomerAccountRow = (row: any): CustomerAccountRow => ({
  id: row.id,
  name: row.name,
  phone: row.phone,
  cuit: row.cuit,
  creditLimit: row.credit_limit,
  balance: row.balance,
  blocked: !!row.blocked,
  updatedAt: row.updated_at,
  debtSince: resolveCustomerDebtSinceFromCache(String(row.id)),
  portalToken: row.token
    ? {
        customerId: row.id,
        token: row.token,
        paymentLinkUrl: row.payment_link_url,
        qrImageUrl: row.qr_image_url,
        expectedAmount: row.expected_amount,
        expiresAt: row.expires_at,
        lastPaymentStatus: row.last_payment_status,
        lastPaymentAmount: row.last_payment_amount,
        lastPaymentAt: row.last_payment_at,
        lastPaymentReference: row.last_payment_reference,
      }
    : null,
  lastReminderAt: row.last_reminder_at,
  email: row.email ?? null,
  address: row.address ?? null,
  taxCategory: row.tax_category ?? null,
  notes: row.notes ?? null,
  paymentTermsDays: row.payment_terms_days != null ? Number(row.payment_terms_days) : null,
  priceListId: row.price_list_id ?? null,
  taxCondition: row.tax_condition != null ? Number(row.tax_condition) : null,
  businessName: row.business_name ?? null,
});

/** En web online, SQLite local suele quedar stale; leemos listados de Customer desde Supabase. */
export function shouldReadCustomerFromRemoteOnWeb(): boolean {
  if (!useAuthStore.getState().session) return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  return true;
}

export async function countCustomerAccountsRemote(query: CustomerAccountListQuery = {}): Promise<number> {
  let remoteQuery = supabase
    .schema('customers')
    .from('customers')
    .select('id', { count: 'exact', head: true });
  const search = query.search?.trim();
  if (search) {
    remoteQuery = remoteQuery.or(
      appendRemoteCustomerSearchOr(search, query.searchExtraCustomerIds)
    );
  }
  if (query.statusFilter === 'has_debt') remoteQuery = remoteQuery.gt('balance', 0);
  else if (query.statusFilter === 'no_debt') remoteQuery = remoteQuery.lte('balance', 0);
  else if (query.statusFilter === 'blocked') remoteQuery = remoteQuery.eq('blocked', true);
  else if (query.statusFilter === 'overdue') remoteQuery = remoteQuery.gt('balance', 0);

  const { count, error: countError } = await remoteQuery;
  if (countError) return 0;
  return count ?? 0;
}

export async function fetchCustomerAccountsRemoteListOnly(opts?: {
  limit?: number;
  offset?: number;
  query?: CustomerAccountListQuery;
}): Promise<CustomerAccountRow[]> {
  const limit = opts?.limit ?? undefined;
  const offset = opts?.offset ?? 0;
  const listQuery = opts?.query ?? {};

  let remoteQuery = supabase
    .schema('customers')
    .from('customers')
    .select('id, name, phone, cuit, credit_limit, balance, blocked, updated_at, price_list_id, tax_condition, business_name');
  const search = listQuery.search?.trim();
  if (search) {
    remoteQuery = remoteQuery.or(
      appendRemoteCustomerSearchOr(search, listQuery.searchExtraCustomerIds)
    );
  }
  if (listQuery.statusFilter === 'has_debt') remoteQuery = remoteQuery.gt('balance', 0);
  else if (listQuery.statusFilter === 'no_debt') remoteQuery = remoteQuery.lte('balance', 0);
  else if (listQuery.statusFilter === 'blocked') remoteQuery = remoteQuery.eq('blocked', true);
  else if (listQuery.statusFilter === 'overdue') remoteQuery = remoteQuery.gt('balance', 0);

  if (listQuery.sortBy === 'balance_desc') remoteQuery = remoteQuery.order('balance', { ascending: false });
  else if (listQuery.sortBy === 'balance_asc') remoteQuery = remoteQuery.order('balance', { ascending: true });
  else if (listQuery.sortBy === 'updated_desc') remoteQuery = remoteQuery.order('updated_at', { ascending: false });
  else if (listQuery.sortBy === 'limit_desc') remoteQuery = remoteQuery.order('credit_limit', { ascending: false });
  else if (listQuery.sortBy === 'limit_asc') remoteQuery = remoteQuery.order('credit_limit', { ascending: true });
  else remoteQuery = remoteQuery.order('name', { ascending: listQuery.sortBy !== 'name_desc' });

  if (limit != null) {
    remoteQuery = remoteQuery.range(offset, offset + limit - 1);
  }
  const { data: customersData, error: customersError } = await remoteQuery;
  if (customersError || !Array.isArray(customersData) || customersData.length === 0) {
    return [];
  }

  return customersData.map((row: any) => ({
    id: String(row.id),
    name: String(row.name ?? ''),
    phone: String(row.phone ?? ''),
    cuit: row.cuit ?? null,
    creditLimit: Number(row.credit_limit ?? 0),
    balance: Number(row.balance ?? 0),
    blocked: Boolean(row.blocked),
    updatedAt: row.updated_at ? String(row.updated_at) : null,
    debtSince: resolveCustomerDebtSinceFromCache(String(row.id)),
    portalToken: null,
    lastReminderAt: null,
    priceListId: row.price_list_id ?? null,
    taxCondition: row.tax_condition != null ? Number(row.tax_condition) : null,
    businessName: row.business_name ?? null,
  }));
}

async function fetchAllLocalCustomerRowsForWebOverlay(): Promise<RemoteCustomerRow[]> {
  // ADR-017 Fase 4: SQLite local eliminado — sin espejo local, usar solo snapshot remoto.
  return [];
}

/**
 * Referencia estable de mora desde la cache del snapshot web (si ya está
 * cargada). null sin snapshot — los callers caen a `updatedAt`.
 */
export function resolveCustomerDebtSinceFromCache(customerId: string): string | null {
  const snapshot = getWebCustomerSnapshotCache();
  if (!snapshot || snapshot.snapshot_complete === false) return null;
  return buildDebtSinceByCustomerIdFromSnapshot(snapshot).get(customerId) ?? null;
}

async function resolveWebCustomerListRows(): Promise<{
  rows: RemoteCustomerRow[];
  snapshotAvailable: boolean;
  debtSinceByCustomerId: Map<string, string>;
}> {
  const snapshot = await loadWebCustomerListSnapshot();
  const local = await fetchAllLocalCustomerRowsForWebOverlay();
  if (!snapshot) {
    return { rows: local, snapshotAvailable: false, debtSinceByCustomerId: new Map() };
  }
  return {
    rows: mergeCustomerSnapshotWithLocal(snapshot.customers, local),
    snapshotAvailable: true,
    debtSinceByCustomerId: buildDebtSinceByCustomerIdFromSnapshot(snapshot),
  };
}

export async function countCustomerAccountsFromWebSnapshot(query: CustomerAccountListQuery = {}): Promise<number> {
  const { rows, snapshotAvailable, debtSinceByCustomerId } = await resolveWebCustomerListRows();
  if (!snapshotAvailable && rows.length === 0) return countCustomerAccountsRemote(query);
  return countCustomerSnapshotRows(rows, query, { debtSinceByCustomerId });
}

export async function fetchCustomerAccountsFromWebSnapshot(opts?: {
  limit?: number;
  offset?: number;
  query?: CustomerAccountListQuery;
}): Promise<CustomerAccountRow[]> {
  const { rows, snapshotAvailable, debtSinceByCustomerId } = await resolveWebCustomerListRows();
  if (!snapshotAvailable && rows.length === 0) return fetchCustomerAccountsRemoteListOnly(opts);
  return fetchCustomerAccountsFromSnapshot(rows, { ...opts, debtSinceByCustomerId });
}

export async function getCustomerSalesForCustomerFromRemote(customerId: string): Promise<
  Array<{
    id: string;
    shortId: string;
    createdAt: string;
    total: number;
    paidAmount: number;
    pendingAmount: number;
  }>
> {
  const snapshot = await loadWebCustomerSnapshot();
  const chargesBySale = new Map<string, { amount: number; createdAt: string }>();

  for (const movement of snapshot.customer_movements) {
    if (movement.customer_id !== customerId || movement.type !== 'charge' || !movement.sale_id) continue;
    chargesBySale.set(movement.sale_id, {
      amount: Number(movement.amount ?? 0),
      createdAt: String(movement.created_at ?? ''),
    });
  }

  const allocatedBySale = new Map<string, number>();
  for (const allocation of snapshot.sale_payment_allocations) {
    if (allocation.voided_at) continue;
    const saleId = String(allocation.sale_id ?? '');
    if (!saleId) continue;
    allocatedBySale.set(saleId, (allocatedBySale.get(saleId) ?? 0) + Number(allocation.amount ?? 0));
  }

  const results: Array<{
    id: string;
    shortId: string;
    createdAt: string;
    total: number;
    paidAmount: number;
    pendingAmount: number;
  }> = [];

  for (const [saleId, charge] of chargesBySale) {
    const allocated = allocatedBySale.get(saleId) ?? 0;
    const pendingFromCharge = charge.amount - allocated;
    if (pendingFromCharge <= 0) continue;

    let total = charge.amount;
    let nonCustomerPaid = 0;
    // ADR-017 Fase 4: SQLite local eliminado — sin lectura local de sales/payments.
    // pendingFromCharge sigue siendo válido como monto pendiente del cargo.

    const paidAmount = allocated + nonCustomerPaid;
    const pendingAmount = Math.max(0, total - paidAmount);
    if (pendingAmount <= 0) continue;

    results.push({
      id: saleId,
      shortId: saleId.slice(-6),
      createdAt: charge.createdAt,
      total,
      paidAmount,
      pendingAmount,
    });
  }

  return results.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

function toCheckText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/** Extrae `metadata.check` del movimiento (null si no hay datos de cheque). */
function mapCheckMetadata(metadata: unknown): CustomerMovementRow['check'] {
  if (!metadata || typeof metadata !== 'object') return null;
  const check = (metadata as Record<string, unknown>).check;
  if (!check || typeof check !== 'object') return null;
  const row = check as Record<string, unknown>;
  return {
    number: toCheckText(row.number),
    dueDate: toCheckText(row.due_date ?? row.dueDate),
    drawer: toCheckText(row.drawer),
    bank: toCheckText(row.bank),
  };
}

export async function getCustomerMovementHistoryFromRemote(
  customerId: string,
  limit: number,
  offset: number,
  filters?: CustomerMovementHistoryFilters
): Promise<CustomerMovementRow[]> {
  const snapshot = await loadWebCustomerSnapshot();
  let rows = snapshot.customer_movements.filter((row) => row.customer_id === customerId);

  if (filters?.fromCreatedAt) {
    rows = rows.filter((row) => String(row.created_at ?? '') >= filters.fromCreatedAt!);
  }
  if (filters?.toCreatedAt) {
    rows = rows.filter((row) => String(row.created_at ?? '') <= filters.toCreatedAt!);
  }
  if (filters?.type && filters.type !== 'all') {
    rows = rows.filter((row) => row.type === filters.type);
  }

  rows.sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')));

  return rows.slice(offset, offset + limit).map((row) => ({
    id: String(row.id),
    amount: Number(row.amount ?? 0),
    type: row.type === 'payment' ? 'payment' : 'charge',
    note: row.note ?? null,
    saleId: row.sale_id ?? null,
    saleShortId: row.sale_id ? String(row.sale_id).slice(-6) : null,
    createdAt: String(row.created_at ?? ''),
    performedByUserId: row.performed_by_user_id ?? null,
    originDeviceId: row.origin_device_id ?? null,
    paymentMethod: row.payment_method ?? null,
    check: mapCheckMetadata(row.metadata),
  }));
}

export const DEFAULT_MOVEMENTS_PAGE_SIZE = 20;

/**
 * Busca clientes duplicados por teléfono (exacto) o nombre (exacto, case-insensitive).
 * Excluye el cliente con `excludeId` (para edición).
 * Retorna hasta 5 matches.
 */
export async function findDuplicateCustomers(opts: {
  phone?: string | null;
  name?: string;
  excludeId?: string;
}): Promise<CustomerAccountRow[]> {
  const { phone, name, excludeId } = opts;
  const phoneTrim = phone?.trim();
  const nameTrim = name?.trim();
  if (!phoneTrim && !nameTrim) return [];

  const conditions: string[] = [];
  if (phoneTrim) conditions.push(`phone.eq.${phoneTrim}`);
  if (nameTrim) conditions.push(`name.ilike.${nameTrim}`);

  let query = supabase
    .schema('customers')
    .from('customers')
    .select('id, name, phone, cuit, credit_limit, balance, blocked, updated_at, price_list_id, tax_condition, business_name, email, address, tax_category, notes, payment_terms_days')
    .or(conditions.join(','))
    .limit(5);
  if (excludeId) query = query.neq('id', excludeId);

  const { data, error } = await query;
  if (error || !Array.isArray(data) || data.length === 0) return [];
  return data.map(mapCustomerAccountRow);
}

export type CustomerSalesSummary = {
  totalSpent: number;
  salesCount: number;
  avgTicket: number;
  firstSaleAt: string | null;
  lastSaleAt: string | null;
};

/**
 * Resumen de ventas de un cliente (gasto total, cantidad, ticket promedio).
 * Usa el RPC `get_customer_sales_summary` (security definer, valida org).
 */
export async function getCustomerSalesSummary(
  customerId: string,
  from?: string,
  to?: string,
): Promise<CustomerSalesSummary | null> {
  const { data, error } = await supabase
    .rpc('get_customer_sales_summary', {
      p_customer_id: customerId,
      p_from: from ?? null,
      p_to: to ?? null,
    });
  if (error || !Array.isArray(data) || data.length === 0) return null;
  const row = data[0];
  return {
    totalSpent: Number(row.total_spent ?? 0),
    salesCount: Number(row.sales_count ?? 0),
    avgTicket: Number(row.avg_ticket ?? 0),
    firstSaleAt: row.first_sale_at ?? null,
    lastSaleAt: row.last_sale_at ?? null,
  };
}

export type CustomerAppointmentPreview = {
  customerId: string;
  lastVisitAt: string | null;
  nextAppointmentAt: string | null;
};

/**
 * Preview batch: última visita y próximo turno para una lista de clientes.
 * Usa el RPC `get_customer_appointments_preview` (security definer, valida org).
 */
export async function getCustomerAppointmentsPreview(
  customerIds: string[],
): Promise<Map<string, CustomerAppointmentPreview>> {
  if (customerIds.length === 0) return new Map();
  const { data, error } = await supabase
    .rpc('get_customer_appointments_preview', { p_customer_ids: customerIds });
  if (error || !Array.isArray(data)) return new Map();
  const map = new Map<string, CustomerAppointmentPreview>();
  for (const row of data) {
    map.set(row.customer_id, {
      customerId: row.customer_id,
      lastVisitAt: row.last_visit_at ?? null,
      nextAppointmentAt: row.next_appointment_at ?? null,
    });
  }
  return map;
}

